import {
  useMutation,
  useQueryClient,
  UseMutationResult,
} from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import axios from 'axios';
import apiConfig from '../../../constants/apiConfig';
import { toast } from 'react-toastify';

import {
  filesOrFoldersKeys,
  FileOrFolder,
  FileScopeType,
} from './useGetAllFiles';
import { fileProcessingStatusKeys } from './useFileProcessingStatus';
import {
  clearUploadProgressMany,
  getPendingUploadTempIds,
  setPendingUploadTempIds,
  setUploadProgress,
} from './uploadProgressStore';

// ========================================
// Type Definitions
// ========================================

export interface UploadFilesPayload {
  files: File[];
  notefolderId?: string | null;
  workspaceId?: string; // Optional override for workspace ID
  scopeType?: FileScopeType; // 'organization' for org-library uploads
  ragIndex?: boolean;
}

export interface UploadedFile {
  fileUrl: string;
  file_name?: string;
  mimetype?: string;
  size?: string;
  id?: string;
  rag_ready?: boolean;
  rag_processed_at?: string | null;
  workspaceId?: string;
  notefolderId?: string | null;
  type?: string;
  createdAt?: string;
  updatedAt?: string;
}

// POST /dice/files — uploads one or more files and creates managed-file rows.
// Pass `ragIndex: false` for transient uploads (IAP form fields, MSEL trigger
// responses) that don't belong in the RAG corpus.
export const uploadFilesToBackend = async ({
  files,
  workspaceId,
  notefolderId,
  scopeType,
  ragIndex,
  onUploadProgress,
}: {
  files: File[];
  workspaceId: string;
  notefolderId?: string | null;
  scopeType?: FileScopeType;
  ragIndex?: boolean;
  onUploadProgress?: (percent: number) => void;
}): Promise<UploadedFile[]> => {
  const formData = new FormData();
  files.forEach((file) => formData.append('file', file));
  formData.append('workspaceId', workspaceId);
  if (notefolderId) {
    formData.append('folderId', notefolderId);
  }
  if (scopeType) {
    formData.append('scopeType', scopeType);
  }
  if (ragIndex === false) {
    formData.append('ragIndex', 'false');
  }
  const response = await axios.post<UploadedFile[]>(
    apiConfig.apiGateway.filesBase,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (event) => {
        if (!onUploadProgress || !event.total) return;
        onUploadProgress(Math.round((event.loaded / event.total) * 100));
      },
    }
  );
  return response.data;
};

function normalizeUploadedFiles(
  uploaded: UploadedFile[],
  workspaceId: string,
  notefolderId?: string | null
): Partial<FileOrFolder>[] {
  return uploaded.map((file) => ({
    ...file,
    id: file.id as UUID,
    file_name: file.file_name || 'Untitled',
    fileUrl: file.fileUrl || '',
    workspaceId: file.workspaceId || workspaceId,
    notefolderId: file.notefolderId ?? notefolderId ?? null,
    type: 'file' as const,
    folder_name: null,
    color: null,
    rag_ready: file.rag_ready ?? false,
    rag_processed_at: file.rag_processed_at ?? null,
    createdAt: file.createdAt || new Date().toISOString(),
    updatedAt: file.updatedAt || new Date().toISOString(),
  }));
}

// ========================================
// Selector Helpers
// ========================================

const selectWorkspaceId = (state: any) =>
  state.prepare?.workspace?.selectedWorkspace;

// ========================================
// Mutations
// ========================================

/** Upload files (consolidated endpoint - single API call) */
export const useUploadFiles = (): UseMutationResult<
  any,
  Error,
  UploadFilesPayload
> => {
  const queryClient = useQueryClient();
  // TODO (aeortic): If we are outside of a workspace context,
  // then this will be incorrect. We are outside of a workspace context
  // when we are creating a situation report for the organization.
  const reduxWorkspaceId = useSelector(selectWorkspaceId);

  return useMutation({
    mutationFn: async ({
      files,
      notefolderId,
      workspaceId: providedWorkspaceId,
      scopeType,
      ragIndex,
    }: UploadFilesPayload) => {
      const workspaceId = providedWorkspaceId || reduxWorkspaceId;

      if (!workspaceId) {
        throw new Error('No workspace ID available');
      }

      if (!files || files.length === 0) {
        throw new Error('No files provided');
      }

      if (files.length > 10) {
        throw new Error('Maximum upload limit is 10 files at a time');
      }

      return await uploadFilesToBackend({
        files,
        workspaceId,
        notefolderId,
        scopeType,
        ragIndex,
        onUploadProgress: (percent) => {
          for (const tempId of getPendingUploadTempIds()) {
            setUploadProgress(tempId, percent);
          }
        },
      });
    },
    // Optimistic update: Show files immediately while upload is in flight
    onMutate: async ({
      files,
      notefolderId,
      workspaceId: providedWorkspaceId,
    }) => {
      const workspaceId = providedWorkspaceId || reduxWorkspaceId;

      // Cancel any outgoing refetches to avoid overwriting optimistic update
      // exact: false used because of optimistic update to prevent race conditions
      // and issues from nested files
      await queryClient.cancelQueries({
        queryKey: filesOrFoldersKeys.allFiles(workspaceId),
        exact: false,
      });

      // Use same query key as useGetAllFiles.ts (no notefolderId in key)
      const queryKey = filesOrFoldersKeys.allFiles(workspaceId);

      // Get previous data for rollback
      const previousData = queryClient.getQueryData(queryKey);

      // Create optimistic file records - using FileOrFolder type from useGetAllFiles
      const optimisticFiles: Partial<FileOrFolder>[] = Array.from(files).map(
        (file, index) => ({
          id: `temp-${Date.now()}-${index}-${Math.random()}` as UUID,
          file_name: file.name,
          fileUrl: '', // No URL yet
          mimetype: file.type,
          size: `${(file.size / 1024).toFixed(2)} kb`,
          workspaceId,
          notefolderId: notefolderId || null,
          type: 'file' as const,
          folder_name: null,
          color: null,
          rag_ready: false, // Show as "Processing"
          rag_processed_at: null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        })
      );

      const tempIds = optimisticFiles.map((f) => String(f.id));
      setPendingUploadTempIds(tempIds);
      tempIds.forEach((id) => setUploadProgress(id, 0));

      // Optimistically update cache
      queryClient.setQueryData(queryKey, (old: any) => {
        // If cache doesn't exist, initialize it with optimistic files
        if (!old?.dataset) {
          return {
            dataset: optimisticFiles,
            status: { msg: 'Files uploaded', action_status: true },
          };
        }

        // Cache exists, append optimistic files
        return {
          ...old,
          dataset: [...old.dataset, ...optimisticFiles],
        };
      });

      // Return context for rollback
      return { previousData, workspaceId, tempIds };
    },
    onSuccess: (data, variables, context) => {
      const workspaceId = variables.workspaceId || reduxWorkspaceId;
      const queryKey = filesOrFoldersKeys.allFiles(workspaceId);
      const uploaded = Array.isArray(data) ? data : [];
      const uploadedNames = new Set(variables.files.map((f) => f.name));
      const normalized = normalizeUploadedFiles(
        uploaded,
        workspaceId,
        variables.notefolderId
      );

      queryClient.setQueryData(queryKey, (old: any) => {
        const existing: FileOrFolder[] = old?.dataset ?? [];
        const withoutMatchingTemps = existing.filter((item) => {
          if (!String(item.id).startsWith('temp-')) return true;
          return !uploadedNames.has(item.file_name ?? '');
        });
        return {
          ...(old ?? {}),
          dataset: [...withoutMatchingTemps, ...normalized],
          status: old?.status ?? {
            msg: 'Files uploaded',
            action_status: true,
          },
        };
      });

      const tempIds = context?.tempIds ?? getPendingUploadTempIds();
      clearUploadProgressMany(tempIds);
      setPendingUploadTempIds([]);

      // Invalidate so list/RAG status stay in sync with the server
      queryClient.invalidateQueries({
        queryKey: filesOrFoldersKeys.allFiles(workspaceId),
        exact: false,
      });
      queryClient.invalidateQueries({
        queryKey: fileProcessingStatusKeys.byWorkspace(workspaceId),
      });
      toast.success('Files uploaded successfully');
    },
    onError: (error: any, variables, context: any) => {
      const tempIds = context?.tempIds ?? getPendingUploadTempIds();
      clearUploadProgressMany(tempIds);
      setPendingUploadTempIds([]);

      // Rollback optimistic update on error
      if (context?.previousData && context?.workspaceId) {
        queryClient.setQueryData(
          filesOrFoldersKeys.allFiles(context.workspaceId),
          context.previousData
        );
      }
      console.error('Error uploading files:', error);
      toast.error(
        error?.response?.data?.error?.message ||
          error?.response?.data?.response?.status?.msg ||
          error?.message ||
          'Failed to upload files'
      );
    },
  });
};

/**
 * Re-run RAG processing for a single stuck file (POST
 * /dice/files/:id/reprocess). The backend no-ops when the file is already
 * indexed or already has a job in flight, so repeated clicks are safe.
 */
export const useReprocessFile = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { fileId: string; workspaceId?: string }) => {
      const response = await axios.post<{ requeued: boolean; reason?: string }>(
        `${apiConfig.apiGateway.filesBase}/${payload.fileId}/reprocess`
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      if (data.requeued) {
        toast.success('File processing restarted');
      } else {
        toast.info('File is already processed or being processed');
      }
      qc.invalidateQueries({
        queryKey: fileProcessingStatusKeys.byWorkspace(variables.workspaceId),
      });
      // rag_ready may have flipped (e.g. already_processed) — refresh the list too.
      qc.invalidateQueries({
        queryKey: filesOrFoldersKeys.allFiles(variables.workspaceId),
        exact: false,
      });
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.error?.message ||
          error?.message ||
          'Failed to restart file processing'
      );
    },
  });
};

/**
 * Re-run RAG processing for every stuck file in a workspace (POST
 * /dice/files/reprocess-all).
 */
export const useReprocessAllFiles = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      workspaceId: string;
      scopeType?: FileScopeType;
    }) => {
      const response = await axios.post<{ requeued: number; stuck: number }>(
        `${apiConfig.apiGateway.filesBase}/reprocess-all`,
        payload
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      toast.success(
        data.requeued > 0
          ? `Restarted processing for ${data.requeued} file${
              data.requeued === 1 ? '' : 's'
            }`
          : 'No stuck files to restart'
      );
      qc.invalidateQueries({
        queryKey: fileProcessingStatusKeys.byWorkspace(variables.workspaceId),
      });
      qc.invalidateQueries({
        queryKey: filesOrFoldersKeys.allFiles(variables.workspaceId),
        exact: false,
      });
    },
    onError: (error: any) => {
      toast.error(
        error?.response?.data?.error?.message ||
          error?.message ||
          'Failed to restart file processing'
      );
    },
  });
};

export const useDeleteFile = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { fileId: string; workspaceId?: string }) => {
      await axios.delete(`${apiConfig.apiGateway.filesBase}/${payload.fileId}`);
    },
    onMutate: async (payload) => {
      if (!payload.workspaceId) return {};

      const queryKey = ['filesOrFolders', payload.workspaceId];
      await qc.cancelQueries({ queryKey });
      const previousData = qc.getQueryData(queryKey);

      // Optimistically remove file
      qc.setQueryData(queryKey, (old: any) => {
        if (!old?.dataset) return old;
        return {
          ...old,
          dataset: old.dataset.filter(
            (item: any) => item.id !== payload.fileId
          ),
        };
      });

      return { previousData, workspaceId: payload.workspaceId };
    },
    onSuccess: () => {
      toast.success('File deleted successfully');
      qc.invalidateQueries({ queryKey: ['notefiles'] });
      qc.invalidateQueries({ queryKey: ['filesOrFolders'] });
    },
    onError: (error: any, variables, context: any) => {
      // Rollback on error
      if (context?.previousData && context?.workspaceId) {
        qc.setQueryData(
          ['filesOrFolders', context.workspaceId],
          context.previousData
        );
      }
      toast.error(
        error?.response?.data?.error?.message ||
          error?.response?.data?.response?.status?.msg ||
          'Failed to delete file'
      );
    },
  });
};
